import React, { useState, useEffect, useRef } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppSelector } from 'app/hooks';
import { checkEnableParseJSON, removeDuplicate } from 'components/helper/helper';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import testingServices from 'services/testingServices';
import familyServices from 'services/familyServices';
import { logout } from 'app/basicInformationSlice/basicInformationSlice';
import { useAppDispatch } from 'app/hooks';
import { routers } from 'router/router';
import { getAccessToken, removeUserId, removeAccessToken } from 'utils/sessionStorage';

const MiniCounselorListMenu = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const [showSubmenu, setShowSubmenu] = useState<string | null>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const menu = useAppSelector((store) => store.counselor.studentList);
  const parentLists = useAppSelector((store) => store.basicInformation.partners);
  const [menuGroups, setMenuGroups] = useState<any[]>([]);
  const [studentId, setStudentId] = useState<Number | undefined>(undefined);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const token = getAccessToken();

  // Handle click outside to close submenu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is outside both container and submenu
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideSubmenu = submenuRef.current && !submenuRef.current.contains(target);
      
      if (isOutsideContainer && isOutsideSubmenu) {
        setShowSubmenu(null);
      }
    };

    // Only add listener when submenu is open
    if (showSubmenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSubmenu]);

  useEffect(() => {
    if (menu?.length) {
      let menuRebuild = menu
        .filter((i) => i.student_info)
        .map((item) => ({
          name: [item.student_info?.first_name, item.student_info?.last_name].join(' '),
          id: item.student_id,
          groupYear: item?.student_info?.year_finish_high_school,
          component: 'ReviewStudent',
          path: `/application/counselor/${item.student_id}`,
        }))
        .sort((a: any, b: any) => {
          const aLower: string = a.name.toLowerCase();
          const bLower: string = b.name.toLowerCase();
          if (aLower < bLower) return -1;
          if (aLower > bLower) return 1;
          return 0;
        })
        .sort((a: any, b: any) => b.groupYear - (a.groupYear ?? 0));

      let menuGroup = removeDuplicate(menuRebuild.map((e: any) => e.groupYear)).map((menuItem) => ({
        name: menuItem,
        isGroup: true,
        children: menuRebuild.filter((e: any) => e.groupYear === menuItem),
      }));
      setMenuGroups(menuGroup);
      
      // Don't auto-open any submenu, only open when user clicks
      setIsInitialLoad(false);
    }
  }, [menu, isInitialLoad]);

  useEffect(() => {
    const genMenuInfo = async () => {
      try {
        if (menuGroups && parentLists) {
          let menuArr = menuGroups;
          for(let i=0; i< menuArr.length; i++) {
            let item = menuArr[i];
            if (item.children) {
              for(let j=0; j< item.children.length; j++) {
                let child = item.children[j];
                for (let k=0; k< parentLists.length; k++) {
                  let obj = parentLists[k];
                  if (child.id === obj.student_id) {
                    child.info = obj;
                  }
                }
              }
            }
          }
          setMenuGroups(menuArr);
        }
      }
      catch(ex){
        console.log('Error: ', ex);
      }      
    }
    genMenuInfo();
  }, [parentLists]);

  const handleIconClick = (route: any) => {
    if (route.isGroup === false) {
      history.push(route.route.path);
    } else {
      setShowSubmenu(showSubmenu === route.name ? null : route.name);
    }
  };

  const handleChildClick = (child: any) => {
    setStudentId(child?.id);    
    history.push(child?.path);
    setShowSubmenu(null);
  };

  const isActiveRoute = (route: any) => {
    const currentPath = location.pathname;
    
    if (route.isGroup === false) {
      return currentPath === route.route.path;
    } else {
      return route.children.some((child: any) => {
        return currentPath === child?.path;
      });
    }
  };

  const isChildActive = (child: any) => {
    return location.pathname === child?.path;
  };

  const getCompletionStatus = async (child: any) => {
    const obj = parentLists && parentLists.find && parentLists.find((item: any) => Number(item.student_id) === Number(child.id));
    if (!obj) return null;

    try {
      const [roleFamily, roleTest] = await Promise.all([
        getRoleFamily(obj?.student_id),
        getRoleTest(obj?.student_id)
      ]);

      let loadedCheckModule: any = checkEnableParseJSON(obj?.notes_array, {
        checkModule: {},
        notes: [],
      })?.checkModule ?? {};
      
      if (loadedCheckModule === null) {
        loadedCheckModule = {};
      }

      let filteredKeys = loadedCheckModule ? Object.keys(loadedCheckModule).filter(key => {
        const [first, second] = key.split('.').map(Number);
        return ((first >= 1 && first <= 7) || first===9) && second >= 1 && second <= 6;
      }) : [];

      filteredKeys.sort();
      filteredKeys_checkRoleTest(filteredKeys, roleTest);
      filteredKeys_checkRoleFamily(filteredKeys, roleFamily);
      
      const allTrue = filteredKeys.length > 0 && filteredKeys.every((key: any) => loadedCheckModule[key][0] === true);
      const allCounselorTrue = filteredKeys.length > 0 && filteredKeys.every((key: any) => loadedCheckModule[key][1] === true);
      
      return { allTrue, allCounselorTrue };
    } catch (ex) {
      console.log('Error getting completion status: ', ex);
      return null;
    }
  };

  const filteredKeys_removeItem = (filteredKeys: string[], keyVal: string) => {
    var index = filteredKeys.findIndex((e: string) => e === keyVal);
    if (index > -1) {
      filteredKeys.splice(index, 1);    
    }
  };

  const filteredKeys_checkRoleTest = (filteredKeys: string[], roleTest: any) => {
    if (roleTest) {
      if (roleTest?.satTest === false) {
        filteredKeys_removeItem(filteredKeys, '4.1');   // SAT Tests
      }
      if (roleTest?.actTest === false) {
        filteredKeys_removeItem(filteredKeys, '4.2');   // ACT Tests
      }
      if (roleTest?.actSubjectTest === false) {      
        filteredKeys_removeItem(filteredKeys, '4.3');   // AP Subject Tests
      }
      if (roleTest?.ibSubjectTest === false) {       
        filteredKeys_removeItem(filteredKeys, '4.4');   // IB Subject Tests
      }
      if (roleTest?.clepTest === false) {         
        filteredKeys_removeItem(filteredKeys, '4.5');   // CLEP Tests 
      }
      if (roleTest?.toeflTest === false) {           
        filteredKeys_removeItem(filteredKeys, '4.6');   //  TOEFL iBT
      }        
      if (roleTest?.ieltsAndPte === false) {
        filteredKeys_removeItem(filteredKeys, '4.7');   //  PTE Academic && IELTS
      }
      if (roleTest?.douLingoTest === false) {
        filteredKeys_removeItem(filteredKeys, '4.8');   //  DuoLingo
      }
    }
  };

  const filteredKeys_checkRoleFamily = (filteredKeys: string[], roleFamily: any) => {
    if (roleFamily) {
      if (roleFamily?.parentNo1 === false ) {
        filteredKeys_removeItem(filteredKeys, '2.2');     // Parent/Guardian 1
      }
      if (roleFamily?.parentNo2 === false ) {
        filteredKeys_removeItem(filteredKeys, '2.3');     // Parent/Guardian 2
      }
      if (roleFamily?.stepParent1 === false || roleFamily?.stepParent2 === false) {
        filteredKeys_removeItem(filteredKeys, '2.5');     // Step-Parent(s)
      }
      if (roleFamily?.siblings === false ) {
        filteredKeys_removeItem(filteredKeys, '2.4');     // Sibling(s)
      }
    }    
  };

  const buildRoleTest = (general: any) => {
    const all_tests_wish_report_array: any = checkEnableParseJSON(
      general?.all_tests_wish_report_array ?? {}
    );
    return {
      satTest: all_tests_wish_report_array?.is_SAT,
      actTest: all_tests_wish_report_array?.is_ACT,
      actSubjectTest: all_tests_wish_report_array?.is_AP_Subject,
      ibSubjectTest: all_tests_wish_report_array?.is_IB_Subject,
      ieltsAndPte: all_tests_wish_report_array?.is_PTE || all_tests_wish_report_array?.is_IELTS,
      toeflTest: all_tests_wish_report_array?.is_TOEFL_Paper || all_tests_wish_report_array?.is_TOEFL,
      douLingoTest: all_tests_wish_report_array?.is_DuoLingo,
      clepTest: all_tests_wish_report_array?.is_CLEP,
    };
  };

  async function getRoleTest(studentId: any) {
    let role: any = {
      satTest: false,
      actTest: false,
      actSubjectTest: false,
      ibSubjectTest: false,
      ieltsAndPte: false,
      toeflTest: false,
      douLingoTest: false,
      clepTest: false,
    };
    try {
      const token = getAccessToken();
      if (token) {
        const general: any = await testingServices.getGeneral(studentId);
        role = buildRoleTest(general);        
      }
      else {
        handleLogout();
      }
    }
    catch(ex){
      console.log('Error: ', ex);
    }   
    return role;
  }
   
  const buildRoleFamily = (myHousehold: any) => {
    const who_in_household_array = checkEnableParseJSON(myHousehold?.who_in_household_array, {});
    let role = {
      parentNo1: false,
      parentNo2: false,
      legalGuardian: false,
      stepParent1: false,
      stepParent2: false,
      siblings: false,
    };
    if (who_in_household_array?.is_parent_guardian) {
      role.parentNo1 = true;
      role.parentNo2 = true;
    }
    if (who_in_household_array?.is_sibbling) {
      role.siblings = true;
    }
  
    if (
      myHousehold?.is_listing_step_parents === 'true' &&
      myHousehold?.how_many_step_parents === '1'
    ) {
      role.stepParent1 = true;
    } else if (myHousehold?.is_listing_step_parents === 'true') {
      role.stepParent1 = true;
      role.stepParent2 = true;
    }
    if (
      myHousehold?.whom_live_permanently === 'Legal Guardian' ||
      myHousehold?.whom_live_permanently === 'Ward of Court/State'
    ) {
      role.legalGuardian = true;
    }
  
    return role;
  };    
  
  async function getRoleFamily(studentId: any) {
    let role: any = {
      parentNo1: false,
      parentNo2: false,
      legalGuardian: false,
      stepParent1: false,
      stepParent2: false,
      siblings: false,
    };
    try {
      const token = getAccessToken();
      if (token) {
        const myHousehold: any = await familyServices.getHousehold(studentId);
        role = buildRoleFamily(myHousehold);
      }
      else {
        handleLogout();
      }
    }
    catch(ex){
      console.log('Error:', ex);
    }
    return role;
  }

  const handleLogout = () => {
    dispatch(logout());
    removeAccessToken();
    removeUserId();
    history.push(routers.login.path);
  };

  const renderSubmenu = (route: any) => {
    if (!showSubmenu || showSubmenu !== route.name) return null;

    return (
      <div
        ref={submenuRef}
        className="position-absolute bg-white border rounded shadow-sm"
        style={{
          left: '60px',
          top: '0',
          minWidth: '250px',
          zIndex: 1001,
          maxHeight: '400px',
          overflowY: 'auto'
        }}
      >
        <div className="p-2">
          <div className="fw-bold text-primary mb-2">{route.name}</div>
          {route.children.map((child: any, i: number) => {
            return (
              <div
                key={i}
                className={`d-flex align-items-center p-2 cursor-pointer rounded mini-submenu-item ${
                  isChildActive(child) ? 'bg-primary text-white' : ''
                }`}
                onClick={() => handleChildClick(child)}
                style={{ cursor: 'pointer' }}
              >
                <span className="me-2">{child?.icon || '👤'}</span>
                <span className="flex-grow-1">{child?.name}</span>
                
                {/* Completion indicators will be loaded asynchronously */}
                <span className="ms-auto">
                  {/* Placeholder for completion status */}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <div 
        ref={containerRef}
        className="h-100 d-flex flex-column bg-light border-end position-relative" 
        style={{ width: '60px' }}
      >
        <div className="flex-grow-1 d-flex flex-column py-2">
          {menuGroups.map((route: any, index: number) => {
            const isActive = isActiveRoute(route);

            return (
              <div key={index} className="position-relative">
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip id={`tooltip-${route.name}`}>
                      <strong>{route.name}</strong>
                    </Tooltip>
                  }
                >
                  <Button
                    variant="outline"
                    className={`d-flex flex-column align-items-center justify-content-center mb-1 mx-1 border-0 position-relative mini-menu-item ${
                      isActive ? 'bg-primary text-white' : 'text-secondary'
                    }`}
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '8px'
                    }}
                    onClick={() => handleIconClick(route)}
                  >
                    <PeopleAltOutlinedIcon fontSize="small" />
                  </Button>
                </OverlayTrigger>
                
                {/* Submenu */}
                {renderSubmenu(route)}
              </div>
            );
          })}
        </div>
      </div>
      <style>{`
        .mini-submenu-item:hover {
          background-color: #f8f9fa !important;
        }
      `}</style>
    </>
  );
};

export default MiniCounselorListMenu;
